import { focusCards, quickTags, siteMeta } from "@/mocks/home";

interface HeroSectionProps {
  onPickTag: (tag: string) => void;
  activeTag: string;
}

export default function HeroSection({ onPickTag, activeTag }: HeroSectionProps) {
  return (
    <section id="top" className="w-full bg-background-50">
      <div className="mx-auto max-w-[1400px] px-4 pt-10 md:px-6 md:pt-14">
        <div className="grid gap-8 border-b border-background-200 pb-10 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-[12px] font-medium text-primary-700">
              <i className="ri-folder-shield-2-line text-sm" />
              深度档案区 · 编号索引可回溯
            </span>
            <h1 className="mt-5 font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-[44px] md:leading-[1.15]">
              {siteMeta.name}
              <span className="mt-2 block text-2xl font-semibold text-foreground-700 md:text-3xl">
                完整档案与深度整理
              </span>
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base md:leading-8">
              {siteMeta.intro}
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <p className="flex items-center gap-2 text-sm font-medium text-foreground-800">
                <i className="ri-earth-line text-base text-primary-600" />
                按话题快速进入档案
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {quickTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => onPickTag(tag)}
                    className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors ${
                      activeTag === tag
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                    }`}
                  >
                    # {tag}
                  </button>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-database-2-line text-sm text-secondary-600" />
                  1,284 条已归档
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-refresh-line text-sm text-secondary-600" />
                  每日 09:00 更新
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 pb-12 pt-8 md:px-6 md:pb-16">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            本期焦点档案
          </h2>
          <p className="flex items-center gap-2 text-xs text-foreground-500 md:text-sm">
            <i className="ri-layout-grid-line text-base text-primary-600" />
            多档案并列 · 编号 / 话题 / 节点数一览
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
          {focusCards.map((card, index) => (
            <article
              key={card.id}
              className="group flex animate-fade-up flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
              style={{ animationDelay: `${index * 80}ms` }}
            >
              <div className="relative w-full overflow-hidden" style={{ height: 200 }}>
                <img
                  src={card.image}
                  alt={`${card.title} 档案封面`}
                  title={`${card.title} · 黑料大全深度整理`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
                />
                <span className="absolute left-3 top-3 flex items-center gap-1 rounded-md bg-background-50/95 px-2 py-1 font-label text-[11px] font-semibold tracking-wide text-primary-800">
                  <i className="ri-price-tag-3-line text-xs" />
                  {card.code}
                </span>
                {card.featured && (
                  <span className="absolute right-3 top-3 rounded-md bg-accent-500 px-2 py-1 text-[11px] font-semibold text-accent-950">
                    本周焦点
                  </span>
                )}
              </div>

              <div className="flex flex-1 flex-col p-4">
                <div className="flex items-center gap-2 text-[11px] text-foreground-500">
                  <span className="rounded bg-secondary-100 px-1.5 py-0.5 font-medium text-secondary-900">
                    {card.category}
                  </span>
                  <span>{card.date}</span>
                </div>
                <h3 className="mt-2.5 font-heading text-[15px] font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                  {card.title}
                </h3>
                <p className="mt-2 line-clamp-3 text-[13px] leading-6 text-foreground-600">
                  {card.summary}
                </p>
                <div className="mt-auto flex items-center justify-between gap-2 border-t border-background-200 pt-3 text-[11px] text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-file-list-3-line text-sm text-primary-500" />
                    {card.evidenceCount} 条证据
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-node-tree text-sm text-secondary-500" />
                    {card.timelineNodes} 个节点
                  </span>
                  <span className="flex items-center gap-1 text-primary-700">
                    热度 {card.heat}
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}